import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Chip, Empty, Input, Select, Sheet, Skeleton, Textarea } from "../components/ui";
import { Trophy, PlusCircle, Calendar, MapPin, ArrowRight, Vote, Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PollList } from "./PollsPage";
import ScoreListCard from "../components/ScoreListCard";

export default function MatchesPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { club } = useAuth();
  const canManage = ["owner", "admin", "manager"].includes(club?.my_role);

  const [tab, setTab] = useState(() => new URLSearchParams(loc.search).get("tab") || "polls"); // polls | matches | scores
  const [matches, setMatches] = useState(null);
  const [scores, setScores] = useState(null);
  const [polls, setPolls] = useState(null);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    ground: "",
    overs: 20,
    format: "T20",
    opponent: "",
    notes: "",
    playing_xi: [],
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: m }, { data: p }, { data: mem }, { data: sc }] = await Promise.all([
        api.get("/matches"),
        api.get("/polls"),
        api.get("/members"),
        api.get("/scores"),
      ]);
      setMatches(m);
      setScores(sc);
      setPolls(p);
      setMembers(mem);
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("new") === "1") setOpen(true);
  }, [loc.search]);

  const toggleXi = (mid) => {
    setForm((f) => {
      const has = f.playing_xi.includes(mid);
      return { ...f, playing_xi: has ? f.playing_xi.filter((x) => x !== mid) : [...f.playing_xi, mid] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/matches", { ...form, overs: Number(form.overs) });
      toast.success("Match created");
      setOpen(false);
      setForm({
        date: new Date().toISOString().slice(0, 10),
        ground: "",
        overs: 20,
        format: "T20",
        opponent: "",
        notes: "",
        playing_xi: [],
      });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="pb-28">
      <TopBar
        title="Matches"
        subtitle={tab === "polls" ? `${polls?.length || 0} polls` : tab === "scores" ? `${scores?.length || 0} scorecards` : `${matches?.length || 0} fixtures`}
        right={tab === "scores" ? (
          <Button onClick={() => nav("/scores/new")} size="sm" data-testid="header-new-score-btn">
            <PlusCircle size={16}/> Score
          </Button>
        ) : canManage && (
          <Button onClick={() => tab === "polls" ? nav("/polls/new") : setOpen(true)} size="sm" data-testid={tab === "polls" ? "header-new-poll-btn" : "add-match-header-btn"}>
            <PlusCircle size={16}/> {tab === "polls" ? "Poll" : "Match"}
          </Button>
        )}
      />

      <div className="px-5">
        <div className="grid grid-cols-3 gap-2 mb-4 p-1 bg-black/[0.04] rounded-2xl">
          <button
            onClick={() => setTab("polls")}
            data-testid="tab-polls"
            className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === "polls" ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`}
          >
            <Vote size={15}/> Polls
          </button>
          <button
            onClick={() => setTab("matches")}
            data-testid="tab-matches"
            className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === "matches" ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`}
          >
            <Trophy size={15}/> Fixtures
          </button>
          <button
            onClick={() => setTab("scores")}
            data-testid="tab-scores"
            className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === "scores" ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`}
          >
            <Activity size={15}/> Scores
          </button>
        </div>

        {tab === "scores" ? (
          scores === null ? (
            <div className="space-y-3">{Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-24"/>)}</div>
          ) : scores.length === 0 ? (
            <Card className="p-0">
              <Empty icon={Activity} title="No scorecards yet" subtitle="Anyone in the club can score a match ball by ball." />
              <div className="p-4 pt-0">
                <Button className="w-full" onClick={() => nav("/scores/new")} data-testid="empty-new-score-btn">
                  <PlusCircle size={16}/> Score a match
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {scores.map((s) => <ScoreListCard key={s.id} s={s} onClick={() => nav(`/scores/${s.id}`)} />)}
            </div>
          )
        ) : tab === "polls" ? (
          polls === null ? (
            <div className="space-y-3">{Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-28"/>)}</div>
          ) : polls.length === 0 ? (
            <Card className="p-0">
              <Empty icon={Vote} title="No polls yet" subtitle="Ask your team who's in, and auto-generate teams." />
              {canManage && (
                <div className="p-4 pt-0">
                  <Button className="w-full" onClick={() => nav("/polls/new")} data-testid="tab-empty-add-poll-btn">
                    <PlusCircle size={16}/> Create match poll
                  </Button>
                </div>
              )}
            </Card>
          ) : (
            <PollList polls={polls} onOpen={(p) => nav(`/polls/${p.id}`)} />
          )
        ) : matches === null ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24"/>)}
          </div>
        ) : matches.length === 0 ? (
          <Card className="p-0">
            <Empty icon={Trophy} title="No matches yet" subtitle="Schedule your first fixture." />
            {canManage && (
              <div className="p-4 pt-0">
                <Button className="w-full" onClick={() => setOpen(true)} data-testid="empty-add-match-btn">
                  <PlusCircle size={16}/> Add match
                </Button>
              </div>
            )}
          </Card>
        ) : (
          <div className="space-y-3">
            {matches.map((m) => {
              const upcoming = m.date >= today;
              return (
                <Card
                  key={m.id}
                  className="py-4 px-5 cursor-pointer"
                  onClick={() => nav(`/matches/${m.id}`)}
                  data-testid={`match-card-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Chip tone={upcoming ? "success" : "default"}>{upcoming ? "Upcoming" : "Past"}</Chip>
                        <Chip tone="dark">{m.format}</Chip>
                      </div>
                      <div className="text-lg font-bold truncate">vs {m.opponent}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-sm text-ink-muted">
                        <span className="flex items-center gap-1"><Calendar size={13}/>{fmtDate(m.date)}</span>
                        <span className="flex items-center gap-1"><MapPin size={13}/>{m.ground}</span>
                      </div>
                    </div>
                    <ArrowRight size={18} className="text-ink-muted mt-1" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Sheet open={open} onClose={() => setOpen(false)} title="New Match">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Opponent *" value={form.opponent} onChange={(e)=>setForm({...form, opponent:e.target.value})} required data-testid="new-match-opponent" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Date *" type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} required data-testid="new-match-date" />
            <Input label="Ground *" value={form.ground} onChange={(e)=>setForm({...form, ground:e.target.value})} required data-testid="new-match-ground" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Format" value={form.format} onChange={(e)=>setForm({...form, format:e.target.value})}>
              {["T10","T15","T20","ODI","Test","Custom"].map(f => <option key={f} value={f}>{f}</option>)}
            </Select>
            <Input label="Overs" type="number" min="1" value={form.overs} onChange={(e)=>setForm({...form, overs:e.target.value})} />
          </div>
          <Textarea label="Notes" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} placeholder="Any details about the match…" />

          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-2">
              Playing XI ({form.playing_xi.length})
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
              {members.map((m) => {
                const active = form.playing_xi.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleXi(m.id)}
                    data-testid={`xi-toggle-${m.id}`}
                    className={`h-11 px-3 rounded-2xl text-sm font-semibold text-left truncate ${active ? "bg-brand text-white" : "bg-black/[0.04] text-ink"}`}
                  >
                    {m.name}
                  </button>
                );
              })}
            </div>
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={saving} data-testid="new-match-submit">
            {saving ? "Saving…" : "Create match"}
          </Button>
        </form>
      </Sheet>
    </div>
  );
}
